/* eslint-disable no-unused-vars */
import Vue from "vue";

import {
  getDatabase,
  ref,
  get,
  set,
  update,
  remove,
  push,
  query,
  child,
  orderByChild,
  limitToLast,
  limitToFirst,
  equalTo,
  onValue,
  onChildAdded,
  onChildRemoved,
  runTransaction,
  serverTimestamp,
  onChildChanged,
  startAt,
  endAt,
} from "firebase/database";

import {
  CREATE_CHAT_CHANNEL,
  FETCH_CHAT_CHANNEL,
  DELETE_CHAT_CHANNEL,
  SEND_MESSAGE_IN_CHAT,
  UPDATE_MESSAGE_IN_CHAT,
  DELETE_MESSAGE_IN_CHAT,
  FETCH_MESSAGES_IN_CHAT,
  UPDATE_UNREAD_COUNT_IN_CHAT,
  UPDATE_META_INFO_IN_CHAT,
} from "../actions";

import {
  SET_CHAT_CHANNEL,
  SET_CHAT_USER,
  SET_CHAT_MESSAGES,
  RESET_CHAT_MESSAGE,
  DEL_CHAT_MESSAGE
} from "../mutations";
import { MESSAGES_IN_PAGE } from '../../constants/community';

const database = getDatabase();

const state = {
  channels: {},
  messages: {},
};

const getters = {
  channels({ channels }) {
    return channels;
  },
  messages({ messages }) {
    return messages;
  }
};

const mutations = {
  [SET_CHAT_CHANNEL](state, {id, ...others}) {
    Vue.set(state.channels, id, { ...others });
  },
  [SET_CHAT_MESSAGES](state, {id, ...others}) {
    Vue.set(state.messages, id, { ...others });
  },
  [RESET_CHAT_MESSAGE](state) {
    state.messages = {};
  },
  [DEL_CHAT_MESSAGE](state, { id }) {
    Vue.delete(state.messages, id);
  },
  [SET_CHAT_USER](state, { id, ...others }) {
    Vue.set(state.users, id, {...others});
  }
  // [DEL_NOTIFICATION_EVENT](state, { id }) {
  //   Vue.delete(state.notifications, id);
  // },
  // [SET_UNREAD_COUNT](state, value) {
  //   state.notifications.unreadCount = value;
  // },
};

const actions = {
  async [CREATE_CHAT_CHANNEL]({ commit }, payload) {
    const {uid, opponentId} = payload;
    const members = [uid, opponentId].sort((a, b) => {
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    });

    const memberIds = { [uid]: true, [opponentId]: true };
    const chatChannelId = `_${members.join("_")}`;

    let snpChats = await get(ref(database, `chats/${chatChannelId}`));
    if (snpChats.exists()) {
      // const data = snpChats.val();
      return chatChannelId;
    }

    const createdAt = serverTimestamp();
    const newChatChannelData = { createdAt, members : memberIds, updatedAt: createdAt, uid: chatChannelId };

    set(ref(database, `chats/${chatChannelId}`), newChatChannelData);
    set(ref(database, `members/${uid}/chats/${chatChannelId}`), { uid: chatChannelId, updatedAt: createdAt, members, active: true, typing: false, unreadCount: 0});
    set(ref(database, `members/${opponentId}/chats/${chatChannelId}`), { uid: chatChannelId, updatedAt: createdAt, members, active: true, typing: false, unreadCount: 0});

    return chatChannelId;
  },
  async [FETCH_CHAT_CHANNEL]({commit, rootGetters}, payload) {
    const { uid } = rootGetters["auth/currentUser"];
    const userChatsRef = ref(database, `members/${uid}/chats`);

    onChildAdded(query(userChatsRef, orderByChild("updatedAt")), async (snapshot) => {
      let channelDoc = snapshot.val();
      let pos = channelDoc.members.findIndex(member => member === uid);
      pos = (pos + 1) % 2;
      const opponent = await get(ref(database, `members/${channelDoc.members[pos]}/public`));
      channelDoc = {...channelDoc, opponent: opponent.val()};
      commit(SET_CHAT_CHANNEL, { id: snapshot.key, ...channelDoc});
    })

    onChildChanged(query(userChatsRef, orderByChild("updatedAt")), async (snapshot) => {
      let channelDoc = snapshot.val();
      let pos = channelDoc.members.findIndex(member => member === uid);
      pos = (pos + 1) % 2;
      const opponent = await get(ref(database, `members/${channelDoc.members[pos]}/public`));
      channelDoc = {...channelDoc, opponent: opponent.val()};
      commit(SET_CHAT_CHANNEL, { id: snapshot.key, ...channelDoc});
    })
  },
  async [FETCH_MESSAGES_IN_CHAT]({ commit, rootGetters }, payload) {
    const { uid } = rootGetters["auth/currentUser"];
    const { chatId, lastData } = payload;

    let messageRef;
    if (lastData) {
      messageRef = query(ref(database, `messages/${chatId}`), endAt(lastData.createdAt), orderByChild('createdAt'), limitToLast(MESSAGES_IN_PAGE));
    } else {
      messageRef = query(ref(database, `messages/${chatId}`), orderByChild('createdAt'), limitToLast(MESSAGES_IN_PAGE));
    }

    const lastMessageRef = query(ref(database, `messages/${chatId}`), limitToLast(1));
    const lastUpdatedMessageRef = query(ref(database, `messages/${chatId}`, orderByChild('updatedAt'), limitToLast(1)));

    const messageData = await get(messageRef);
    let hasNext = true;
    if (messageData.exists()) {
      const resultMessages = Object.entries(messageData.val());
      resultMessages.forEach(message => {
        message[1] = {...message[1], uid: message[0]};
        commit(SET_CHAT_MESSAGES, { id: message[0], ...message[1] });
      });
      if (resultMessages.length < MESSAGES_IN_PAGE) {
        hasNext = false;
      }
    } else {
      hasNext = false;
      commit(RESET_CHAT_MESSAGE);
    }

    runTransaction(ref(database, `members/${uid}/chats/${chatId}/unreadCount`), (count) => {
      return 0;
    });

    onChildAdded(lastMessageRef, (snapshot) => {
      commit(SET_CHAT_MESSAGES, { id: snapshot.key, ...snapshot.val() });
    });

    onChildChanged(lastUpdatedMessageRef, (snapshot) => {
      commit(SET_CHAT_MESSAGES, { id: snapshot.key, ...snapshot.val() });
    });

    onChildRemoved(messageRef, (snapshot) => {
      commit(DEL_CHAT_MESSAGE, {id: snapshot.key});
    });
    return hasNext;
  },
  async [SEND_MESSAGE_IN_CHAT]({commit, rootGetters}, payload) {
    const {uid} = rootGetters["auth/currentUser"];
    // const {total, page} = rootGetters["community/queryResult"];
    const {chatId, messageObj, opponentId} = payload;
    const messageId = push(ref(database, `messages/${chatId}`)).key;

    const createdAt = serverTimestamp();
    messageObj.createdAt = createdAt;
    messageObj.sender = uid;


    runTransaction(ref(database, `members/${opponentId}/chats/${chatId}/unreadCount`), (count) => {
      if (count) {
        count++;
      } else {
        count = 1;
      }
      return count;
    });
    update(ref(database, `members/${opponentId}/chats/${chatId}/`), {typing: false});
    set(ref(database, `messages/${chatId}/${messageId}`), messageObj);
    // commit(SET_QUERY_RESULT, { type: "message", total: total + 1, page, limit: MESSAGES_IN_PAGE });
  },
  async [UPDATE_MESSAGE_IN_CHAT]({commit, rootGetters}, payload) {
    const {chatId, messageObj, messageId} = payload;
    const {uid} = rootGetters["auth/currentUser"];
    const createdAt = serverTimestamp();
    messageObj.updatedAt = createdAt;
    messageObj.sender = uid;

    update(ref(database, `messages/${chatId}/${messageId}`), messageObj);
  },
  async [DELETE_MESSAGE_IN_CHAT]({commit, rootGetters}, payload) {
    const {chatId, messageId} = payload;
    // const {total, page} = rootGetters["community/queryResult"];
    remove(ref(database, `messages/${chatId}/${messageId}`));
    // commit(SET_QUERY_RESULT, { type: "message", total: total - 1, page, limit: MESSAGES_IN_PAGE });
  },
  async [UPDATE_UNREAD_COUNT_IN_CHAT]({commit, rootGetters}, payload) {
    const {userId, chatId} = payload;
    runTransaction(ref(database, `members/${userId}/chats/${chatId}/unreadCount`), (count) => {
      return 0;
    });
  },
  async [UPDATE_META_INFO_IN_CHAT]({rootGetters}, payload) {
    const { userId, chatId } = payload;
    let { updateData } = payload;
    const updatedAt = serverTimestamp();
    updateData = {...updateData, updatedAt};
    update(ref(database, `members/${userId}/chats/${chatId}`), updateData);
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
